import Vue from 'vue'
import Vuex from 'vuex'
import login from './modules/login.js'
import generalInfo from './modules/generalInfo.js'
import results from './modules/results.js'
import urlParameters from './modules/urlParameters.js'
import extentHistory from './modules/extentHistory.js'
import featureDetails from './modules/featureDetails.js'
import OLPlaceholderStore from './modules/OLPlaceholderStore.js'
import filters from './modules/tocModules/filters.js'
import labeling from './modules/tocModules/labeling.js'
import symbology from './modules/tocModules/symbology.js'
import views from './modules/views.js'
import settings from './modules/settings.js'
import axios from 'axios'
import edit from './modules/featuresForEdit.js'

Vue.use(Vuex)

const store = new Vuex.Store({
  state: {
    proxyUrl: (process.env.VUE_APP_OL_PROXY_URL.startsWith('http://') || process.env.VUE_APP_OL_PROXY_URL.startsWith('https://') ? process.env.VUE_APP_OL_PROXY_URL : window.origin + process.env.VUE_APP_OL_PROXY_URL) + '?',
    activeTool: null,
    centerActiveTool: null,
    viewType: '2d',
    configJson: {},
    nonMutatedConfigJson: {},
    errorMessage: '',
    layersLoaded: false,
    initialExtent: null,
    mapUpdating: false,
    identifyLoading: false,
    language: null,
    hasDisclaimer: false,
    validLicense: true,
    highlightColor: { default: { rgba: { r: 240, g: 29, b: 189, a: 1 } } },
    highlightLineWidth: 5,
    highlightPolygonOutlineWidth: 6,
    highlightPolygonFillStyle: 'none',
    previouslyOpenedWidgets: {},
    activeMapInteractionWidget: 'Identify',
    snackbar: {
      isShown: false,
      color: null,
      code: null,
      customText: '',
      hideBtn: false,
      timeout: '3000',
    },
    // using this so that the snapping manager logic loading happens when a widget is opened
    // and not when a drawing tool is selected, which would cause initial (and only initial) drawing to begin with a 1-2 second delay
    canActiveWidgetDraw: false,
    isIdentificationBlocked: false,
    mapScale: 0,
    textSearchOption: {
      mode: null,
      radioButton: null,
      service: null,
      layer: null,
    },
    activeSubLayer: null,
    newTocItem: null,
    externalJs: {},
    importedLayers: {},
  },
  getters: {
    getProxyUrl: state => state.proxyUrl,
    getActiveTool: state => state.activeTool,
    getCenterActiveTool: state => state.centerActiveTool,
    getConfigJson: state => state.configJson,
    getNonMutatedConfigJson: state => state.nonMutatedConfigJson,
    getViewType: state => state.viewType,
    getErrorMessage: state => state.errorMessage,
    getLayersLoaded: state => state.layersLoaded,
    getInitialExtent: state => state.initialExtent,
    getMapUpdating: state => state.mapUpdating,
    getIdentifyLoading: state => state.identifyLoading,
    getLanguage: state => state.language,
    getHasDisclaimer: state => state.hasDisclaimer,
    getValidLicense: state => state.validLicense,
    getHighlightColor: state => state.highlightColor,
    getHighlightLineWidth: state => state.highlightLineWidth,
    getHighlightPolygonOutlineWidth: state => state.highlightPolygonOutlineWidth,
    getHighlightPolygonFillStyle: state => state.highlightPolygonFillStyle,
    getPreviouslyOpenedWidgets: state => state.previouslyOpenedWidgets,
    getActiveMapInteractionWidget: state => state.activeMapInteractionWidget,
    getSnackbar: state => state.snackbar,
    getCanActiveWidgetDraw: state => state.canActiveWidgetDraw,
    getIsIdentificationBlocked: state => state.isIdentificationBlocked,
    getMapScale: state => state.mapScale,
    getTextSearchOption: state => state.textSearchOption,
    getActiveSubLayer: state => state.activeSubLayer,
    getNewTocItem: state => state.newTocItem,
    getExternalJs: state => state.externalJs,
    getImportedLayers: state => state.importedLayers,
  },
  mutations: {
    setActiveTool: (state, activeTool) => {
      state.activeTool = activeTool
      if (activeTool) {
        state.previouslyOpenedWidgets = { ...state.previouslyOpenedWidgets, [activeTool]: true }
      }
      const drawTools = ['measure', 'spatialselection', 'draw', 'editor']
      state.canActiveWidgetDraw = drawTools.includes(activeTool.toLowerCase())
    },
    setCenterActiveTool: (state, centerActiveTool) => {
      state.centerActiveTool = centerActiveTool
    },
    unSetActiveTool: (state) => {
      state.activeTool = null
      state.canActiveWidgetDraw = false
    },
    unSetCenterActiveTool: (state) => {
      state.centerActiveTool = null
    },
    setViewType: (state, viewType) => {
      state.viewType = viewType
    },
    setConfigJson: (state, configJson) => {
      if (!configJson.appConfig.graphicLayersInGraphicsManager) {
        configJson.appConfig.graphicLayersInGraphicsManager = []
      }
      state.configJson = configJson
      state.nonMutatedConfigJson = JSON.parse(JSON.stringify(configJson))
    },
    setConfigJsonProperty: (state, propNameAndVal) => {
      state.configJson[propNameAndVal.name] = propNameAndVal.val
    },
    setErrorMessage: (state, em) => {
      state.errorMessage = em
    },
    setLayersLoaded (state, layersLoaded) {
      state.layersLoaded = layersLoaded
    },
    setInitialExtent: (state, extent) => {
      state.initialExtent = extent
    },
    setMapUpdating (state, mapUpdating) {
      state.mapUpdating = mapUpdating
    },
    setIdentifyLoading (state, identifyLoading) {
      state.identifyLoading = identifyLoading
    },
    setLanguage: (state, payload) => {
      state.language = payload.language
      const localization = {
        language: payload.language,
      }
      localStorage.setItem(payload.host + '-' + payload.viewer + '-language', JSON.stringify(localization))
    },
    setHasDisclaimer (state, hasDisclaimer) {
      state.hasDisclaimer = hasDisclaimer
    },
    setValidLicense (state, valid) {
      state.validLicense = valid
    },
    setHighlightColor (state, payload) {
      state.highlightColor = { ...state.highlightColor, [payload.layerName]: { ...payload.color } }
      localStorage.setItem(payload.host + '-' + payload.viewer + '-highlightColors', JSON.stringify(state.highlightColor))
    },
    setDefaultHighlightColor (state, colorObject) {
      state.highlightColor = { ...state.highlightColor, default: { rgba: { ...colorObject } } }
    },
    setDefaultHighlightLineWidth (state, payload) {
      state.highlightLineWidth = payload
    },
    setDefaultHighlightPolygonOutlineWidth (state, payload) {
      state.highlightPolygonOutlineWidth = payload
    },
    setDefaultHighlightPolygonFillStyle (state, payload) {
      state.highlightPolygonFillStyle = payload
    },
    setHighlightColorsFromLocalStorage (state, payload) {
      state.highlightColor = JSON.parse(payload)
    },
    setActiveMapInteractionWidget (state, widgetName) {
      state.activeMapInteractionWidget = widgetName
    },
    unsetCurrentWidgetFromMapInteraction (state, widgetName) {
      if (widgetName === state.activeMapInteractionWidget) {
        state.activeMapInteractionWidget = 'Identify'
      }
    },
    hideSnackbar (state) {
      state.snackbar.isShown = false
    },
    showSnackbar (state, params) {
      state.snackbar = {
        isShown: true,
        color: params.color,
        code: params.code,
        customText: params.customText,
        hideBtn: params.hideBtn,
        timeout: params.timeout || '3000',
      }
    },
    addPreviouslyOpenedWidget (state, widgetName) {
      state.previouslyOpenedWidgets = { ...state.previouslyOpenedWidgets, [widgetName]: true }
    },
    setIsIdentificationBlocked (state, boolVal) {
      state.isIdentificationBlocked = boolVal
    },
    setMapScale (state, scaleDouble) {
      state.mapScale = scaleDouble
    },
    saveTextSearchOptionToLocalStorage (state, payload) {
      const localStoragePropName = `searchText-${store.getters.getViewerName}` + (store.getters.getLoggedInUser?.username ? `-${store.getters.getLoggedInUser.username}` : '')
      state.textSearchOption.mode = payload.mode
      state.textSearchOption.radioButton = payload.radioButton
      localStorage.setItem(localStoragePropName, JSON.stringify(payload))
    },
    setActiveSubLayer (state, value) {
      state.activeSubLayer = value
    },
    setNewTocItem (state, newTocItem) {
      state.newTocItem = newTocItem
    },
    setExternalJs (state, newItem) {
      state.externalJs = newItem
    },
    setImportedLayers (state, importedLayers) {
      state.importedLayers = importedLayers
    },
    addImportedLayer (state, importedLayer) {
      state.importedLayers = {
        ...state.importedLayers,
        ...importedLayer,
      }
    },
    removeImportedLayer (state, importedLayer) {
      Vue.delete(state.importedLayers, importedLayer)
    },
    // could use Vuex functionality to reset the whole state, but maybe we don't want the whole state to be reset
    // that's why we reset some properties of the state manually
    resetVuex () {
      store.dispatch('doLogout')
      store.dispatch('setLayersLoaded', false)
      store.dispatch('setQuery', null)
      store.dispatch('setBookmark', null)
      store.dispatch('setImportedLayers', [])
    },
  },
  actions: {
    setActiveTool: ({ commit }, activeTool) => {
      // tool is started, write a log
      axios.post('/addToolLog', {
        activeTool: activeTool,
        viewerfk: store.getters.getViewerId,
        userfk: store.getters.getLoggedInUser?.id,
      }, { headers: { Authorization: 'Bearer ' + store.getters.getAccessToken } })
      commit('setActiveTool', activeTool)
    },
    storeCenterActiveTool: ({ commit }, centerActiveTool) => {
      // tool is started, write a log
      axios.post('/addToolLog', {
        activeTool: centerActiveTool,
        viewerfk: store.getters.getViewerId,
        userfk: store.getters.getLoggedInUser?.id,
      }, { headers: { Authorization: 'Bearer ' + store.getters.getAccessToken } })
      commit('setCenterActiveTool', centerActiveTool)
    },
    unSetActiveTool: ({ commit }) => {
      commit('unSetActiveTool')
    },
    unStoreCenterActiveTool: ({ commit }) => {
      commit('unSetCenterActiveTool')
    },
    setViewType: ({ commit }, viewType) => {
      commit('setViewType', viewType)
    },
    setConfigJson: ({ commit }, userObject) => {
      commit('setConfigJson', userObject)
    },
    setConfigJsonProperty: ({ commit }, propNameAndVal) => {
      commit('setConfigJsonProperty', propNameAndVal)
    },
    setErrorMessage: ({ commit }, errorMessage) => {
      commit('setErrorMessage', errorMessage)
    },
    setLayersLoaded ({ commit }, layersLoaded) {
      commit('setLayersLoaded', layersLoaded)
    },
    setInitialExtent: ({ commit }, extent) => {
      commit('setInitialExtent', extent)
    },
    setMapUpdating ({ commit }, mapUpdating) {
      commit('setMapUpdating', mapUpdating)
    },
    setIdentifyLoading ({ commit }, identifyLoading) {
      commit('setIdentifyLoading', identifyLoading)
    },
    setLanguage: ({ commit }, payload) => {
      commit('setLanguage', payload)
    },
    setHasDisclaimer ({ commit }, hasDisclaimer) {
      commit('setHasDisclaimer', hasDisclaimer)
    },
    setValidLicense ({ commit }, valid) {
      commit('setValidLicense', valid)
    },
    setHighlightColor ({ commit }, rgb) {
      commit('setHighlightColor', rgb)
    },
    setDefaultHighlightColor ({ commit }, rgb) {
      commit('setDefaultHighlightColor', rgb)
    },
    setDefaultHighlightLineWidth ({ commit }, width) {
      commit('setDefaultHighlightLineWidth', width)
    },
    setDefaultHighlightPolygonOutlineWidth ({ commit }, width) {
      commit('setDefaultHighlightPolygonOutlineWidth', width)
    },
    setDefaultHighlightPolygonFillStyle ({ commit }, style) {
      commit('setDefaultHighlightPolygonFillStyle', style)
    },
    setHighlightColorsFromLocalStorage ({ commit }, highlightColors) {
      commit('setHighlightColorsFromLocalStorage', highlightColors)
    },
    setActiveMapInteractionWidget ({ commit }, widgetName) {
      commit('setActiveMapInteractionWidget', widgetName)
    },
    unsetCurrentWidgetFromMapInteraction ({ commit }, widgetName) {
      commit('unsetCurrentWidgetFromMapInteraction', widgetName)
    },
    showSnackbar ({ commit }, params) {
      commit('showSnackbar', params)
    },
    hideSnackbar ({ commit }) {
      commit('hideSnackbar')
    },
    addPreviouslyOpenedWidget ({ commit }, widgetName) {
      commit('addPreviouslyOpenedWidget', widgetName)
    },
    setIsIdentificationBlocked ({ commit }, boolVal) {
      commit('setIsIdentificationBlocked', boolVal)
    },
    resetVuex ({ commit }) {
      commit('resetVuex')
    },
    mapViewScale ({ commit }, scaleDouble) {
      commit('setMapScale', scaleDouble)
    },
    saveTextSearchOptionToLocalStorage ({ commit }, payload) {
      commit('saveTextSearchOptionToLocalStorage', payload)
    },
    setActiveSubLayer ({ commit }, sublayer) {
      commit('setActiveSubLayer', sublayer)
    },
    setNewTocItem ({ commit }, newTocItem) {
      commit('setNewTocItem', newTocItem)
    },
    storeExternalJs ({ commit }, newItem) {
      commit('setExternalJs', newItem)
    },
    setImportedLayers ({ commit }, importedLayers) {
      commit('setImportedLayers', importedLayers)
    },
    addImportedLayer ({ commit }, importedLayer) {
      commit('addImportedLayer', importedLayer)
    },
    removeImportedLayer ({ commit }, importedLayer) {
      commit('removeImportedLayer', importedLayer)
    },
  },
  modules: {
    login,
    generalInfo,
    results,
    urlParameters,
    extentHistory,
    featureDetails,
    OLPlaceholderStore,
    filters,
    labeling,
    symbology,
    views,
    settings,
    edit,
  },
})

export default store
